import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Mail, Phone, Building2, CheckCircle2, Copy, Loader2, ClipboardList } from "lucide-react";
import { DiscoveryFields } from "@/components/DiscoveryFields";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const PLANS = [
  { key: "core", label: "RetainProof Core" },
  { key: "performance", label: "RetainProof Performance" },
  { key: "executive", label: "RetainProof Executive Oversight" },
];

const STATUS_STYLES = {
  new: "bg-[#174A7E]/10 text-[#174A7E] border-[#174A7E]/30",
  contacted: "bg-[#8A6A1F]/10 text-[#8A6A1F] border-[#8A6A1F]/30",
  qualified: "bg-[#0E7C7B]/10 text-[#0E7C7B] border-[#0E7C7B]/30",
  won: "bg-[#156F52]/10 text-[#156F52] border-[#156F52]/30",
  lost: "bg-[#B54747]/10 text-[#B54747] border-[#B54747]/30",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

const seedFromInquiry = (r) => ({
  office_locations: r.locations || "",
  monthly_spend: r.monthly_spend || "",
  vendor_count: r.vendor_count || "",
  case_system: r.case_system || "",
  call_tracking: r.call_tracking || "",
  reason_for_exploring: r.reporting_challenges || r.message || "",
});

export default function Inquiries() {
  const [rows, setRows] = useState(null);
  const [target, setTarget] = useState(null); // inquiry being converted
  const [form, setForm] = useState({ firm_name: "", name: "", email: "", plan: "core" });
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState(null); // { email, temporary_password }
  const [demo, setDemo] = useState(null);
  const [discTarget, setDiscTarget] = useState(null); // inquiry whose discovery we're editing
  const [discData, setDiscData] = useState({});
  const [savingDisc, setSavingDisc] = useState(false);

  const load = () => {
    api.get("/admin/audit-inquiries").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/admin/analytics/demo").then((r) => setDemo(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    const prev = rows;
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    try {
      await api.patch(`/admin/audit-inquiries/${id}`, { status });
      toast.success("Status updated");
    } catch (err) {
      setRows(prev);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openConvert = (r) => {
    setResult(null);
    setForm({ firm_name: r.firm_name || "", name: r.name || "", email: r.email || "", plan: "core" });
    setTarget(r);
  };

  const doConvert = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/admin/audit-inquiries/${target.id}/convert`, form);
      setResult(data);
      load();
      toast.success("Firm created");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setConverting(false);
    }
  };

  const openDiscovery = (r) => {
    const existing = r.discovery && Object.keys(r.discovery).length ? r.discovery : seedFromInquiry(r);
    setDiscData(existing);
    setDiscTarget(r);
  };

  const saveDiscovery = async () => {
    setSavingDisc(true);
    try {
      await api.put(`/admin/audit-inquiries/${discTarget.id}/discovery`, { discovery: discData });
      setRows((rs) => rs.map((r) => (r.id === discTarget.id ? { ...r, discovery: discData } : r)));
      toast.success("Discovery saved — will auto-populate the firm profile on conversion");
      setDiscTarget(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingDisc(false);
    }
  };

  const openCount = rows ? rows.filter((r) => !["won", "lost"].includes(r.status)).length : 0;

  return (
    <div>
      <div className="mb-8">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Pipeline</p>
        <h1 className="font-display text-4xl font-light mt-1">Leads &amp; Inquiries</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Consultation &amp; audit requests from your public site. Capture discovery answers during the call, update status as you work each one, and convert won leads into a paid firm — the discovery record auto-populates the new firm's profile.
        </p>
        {rows && (
          <div className="mt-3 flex items-center gap-2" data-testid="inquiries-summary">
            <span className="font-mono-data text-[10px] uppercase tracking-widest bg-zinc-100 text-zinc-700 px-2 py-1">{rows.length} total</span>
            <span className="font-mono-data text-[10px] uppercase tracking-widest bg-[#174A7E]/10 text-[#174A7E] px-2 py-1">{openCount} open</span>
          </div>
        )}
      </div>

      {demo && (
        <div className="mb-8" data-testid="demo-analytics">
          <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Demo video funnel</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: "Opens", v: demo.counts.demo_open },
              { label: "Played", v: demo.counts.demo_play },
              { label: "50% watched", v: demo.counts.demo_p50 },
              { label: "Completed", v: demo.counts.demo_complete },
              { label: "Opens · 7d", v: demo.opens_7d },
            ].map((s) => (
              <div key={s.label} className="border border-zinc-200 bg-white p-4">
                <p className="font-display text-3xl">{s.v ?? 0}</p>
                <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 border border-[#0B1F33] bg-[#0B1F33] text-white p-4 flex items-baseline gap-3" data-testid="demo-conversion">
            <span className="font-display text-4xl text-[#D5A44A]">{demo.conversion_rate ?? 0}%</span>
            <span className="text-sm text-white/70">
              of demo viewers sign up
              <span className="text-white/40"> · {demo.demo_conversions ?? 0} of {demo.unique_openers ?? 0} openers</span>
            </span>
          </div>
        </div>
      )}

      {!rows ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="inquiries-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Received</TableHead>
                <TableHead>Firm / Contact</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead className="w-40">Status</TableHead>
                <TableHead className="w-56">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const hasDiscovery = r.discovery && Object.keys(r.discovery).length > 0;
                return (
                  <TableRow key={r.id} className="hover:bg-zinc-50 align-top" data-testid={`inquiry-row-${r.id}`}>
                    <TableCell className="font-mono-data text-xs text-zinc-500 whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                    <TableCell>
                      <p className="font-medium">{r.firm_name || "—"}</p>
                      <p className="text-sm text-zinc-600">{r.name}</p>
                      <a href={`mailto:${r.email}`} className="mt-1 flex items-center gap-1.5 text-xs text-[#174A7E] hover:underline">
                        <Mail className="h-3 w-3" /> {r.email}
                      </a>
                      {r.phone && (
                        <span className="flex items-center gap-1.5 text-xs text-zinc-500"><Phone className="h-3 w-3" /> {r.phone}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-zinc-600 whitespace-nowrap">
                      {r.vendor_count && <div>{r.vendor_count} vendors</div>}
                      {r.months_of_data && <div>{r.months_of_data} mo data</div>}
                      {r.locations && <div>{r.locations} locations</div>}
                      {!r.vendor_count && !r.months_of_data && !r.locations && "—"}
                    </TableCell>
                    <TableCell>
                      <Select value={r.status || "new"} onValueChange={(v) => setStatus(r.id, v)} disabled={!!r.converted_firm_id}>
                        <SelectTrigger className={`rounded-none h-9 border ${STATUS_STYLES[r.status || "new"]}`} data-testid={`inquiry-status-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => openDiscovery(r)}
                          size="sm"
                          variant="outline"
                          className={`rounded-none h-9 justify-start ${hasDiscovery ? "border-[#156F52] text-[#156F52]" : "border-zinc-300 text-zinc-700"}`}
                          data-testid={`inquiry-discovery-${r.id}`}
                        >
                          <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> {hasDiscovery ? "Discovery ✓" : "Discovery"}
                        </Button>
                        {r.converted_firm_id ? (
                          <span className="inline-flex items-center gap-1.5 font-mono-data text-[10px] uppercase tracking-widest text-[#156F52]" data-testid={`inquiry-converted-${r.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5" /> Converted
                          </span>
                        ) : (
                          <Button onClick={() => openConvert(r)} size="sm" className="rounded-none bg-[#0B1F33] text-white hover:bg-zinc-800 h-9 justify-start" data-testid={`inquiry-convert-${r.id}`}>
                            <Building2 className="h-3.5 w-3.5 mr-1.5" /> Convert to firm
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-zinc-500 py-12" data-testid="inquiries-empty">
                    No inquiries yet. They'll appear here when firms submit a request from your site.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Discovery dialog */}
      <Dialog open={!!discTarget} onOpenChange={(o) => { if (!o) setDiscTarget(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="discovery-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[#174A7E]" /> Discovery — {discTarget?.firm_name || discTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-500">Capture answers from the Baseline Consultation. These auto-populate the firm's profile when you convert this lead.</p>
          <div className="py-2">
            <DiscoveryFields value={discData} onChange={setDiscData} />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-none border-zinc-300"
              data-testid="download-findings-button"
              onClick={async () => {
                try {
                  const res = await api.post(`/admin/audit-inquiries/${discTarget.id}/findings-pdf`, { discovery: discData }, { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "retainproof-findings.pdf";
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Findings PDF downloaded");
                } catch {
                  toast.error("PDF export failed");
                }
              }}
            >
              Findings PDF
            </Button>
            <Button onClick={saveDiscovery} disabled={savingDisc} className="rounded-none bg-[#0B1F33] text-white hover:bg-zinc-800" data-testid="save-discovery-button">
              {savingDisc && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save discovery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert dialog */}
      <Dialog open={!!target} onOpenChange={(o) => { if (!o) { setTarget(null); setResult(null); } }}>
        <DialogContent className="max-w-md" data-testid="convert-dialog">
          {result ? (
            <div>
              <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-[#156F52]" /> Firm created</DialogTitle></DialogHeader>
              <p className="text-sm text-zinc-600 mt-2">Share these login details with the client. They should change the password after signing in. Their discovery answers are now on the firm's profile.</p>
              <div className="mt-4 border border-zinc-200 divide-y divide-zinc-200 font-mono-data text-sm">
                <div className="flex justify-between px-3 py-2"><span className="text-zinc-500">Email</span><span data-testid="convert-result-email">{result.email}</span></div>
                <div className="flex justify-between px-3 py-2"><span className="text-zinc-500">Temp password</span><span data-testid="convert-result-password">{result.temporary_password}</span></div>
              </div>
              <Button
                onClick={() => { navigator.clipboard?.writeText(`Email: ${result.email}\nTemporary password: ${result.temporary_password}`); toast.success("Credentials copied"); }}
                variant="outline" className="mt-4 rounded-none w-full" data-testid="convert-copy-credentials"
              >
                <Copy className="h-4 w-4 mr-2" /> Copy credentials
              </Button>
              <DialogFooter className="mt-4">
                <Button onClick={() => { setTarget(null); setResult(null); }} className="rounded-none bg-[#0B1F33] text-white" data-testid="convert-done">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <div>
              <DialogHeader><DialogTitle className="font-display text-2xl">Convert to firm</DialogTitle></DialogHeader>
              <p className="text-sm text-zinc-600 mt-2">Create a paid firm and owner account from this lead. A temporary password will be generated, and any discovery answers become the firm's profile.</p>
              <div className="space-y-4 py-3">
                <div><Label>Firm name</Label><Input value={form.firm_name} onChange={(e) => setForm({ ...form, firm_name: e.target.value })} data-testid="convert-firm-input" className="mt-1 rounded-none" /></div>
                <div><Label>Owner name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="convert-name-input" className="mt-1 rounded-none" /></div>
                <div><Label>Owner email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="convert-email-input" className="mt-1 rounded-none" /></div>
                <div>
                  <Label>Plan</Label>
                  <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                    <SelectTrigger className="mt-1 rounded-none h-11" data-testid="convert-plan-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PLANS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={doConvert} disabled={converting || !form.firm_name || !form.name || !form.email} className="rounded-none bg-[#0B1F33] text-white hover:bg-zinc-800" data-testid="convert-submit">
                  {converting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create firm
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
